/*
* Copyright (c) 2009-2022. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

export function isRectangle(geometry) {
  if (geometry.getLinearRingCount() !== 1 || geometry.getCoordinates()[0].length !== 5) {
    return false;
  }

  const coordinates = geometry.getCoordinates(false)[0];
  let prevX = coordinates[0][0];
  let prevY = coordinates[0][1];
  for (let i = 1; i <= 4; i++) {
    let x = coordinates[i][0];
    let y = coordinates[i][1];
    let xChanged = (x !== prevX);
    let yChanged = (y !== prevY);
    if (xChanged === yChanged) {
      return false;
    }
    prevX = x;
    prevY = y;
  }

  return true;
}

// Rectangle test working directly on a WKT POLYGON string (the annotation panel only
// has the stored `location` WKT, not an OpenLayers geometry). Mirrors isRectangle():
// a single ring of 5 coordinates whose edges alternate between horizontal and vertical.
export function isRectangleWKT(wkt) {
  const match = wkt.match(/POLYGON\s*\(\(\s*([^()]*?)\s*\)\)/i);
  if (!match) {
    return false;
  }

  const points = match[1].split(',').map(pair => pair.trim().split(/\s+/).map(Number));
  if (points.length !== 5) {
    return false;
  }

  let prevX = points[0][0];
  let prevY = points[0][1];
  for (let i = 1; i <= 4; i++) {
    const x = points[i][0];
    const y = points[i][1];
    const xChanged = (x !== prevX);
    const yChanged = (y !== prevY);
    if (xChanged === yChanged) {
      return false;
    }
    prevX = x;
    prevY = y;
  }

  return true;
}

// Coarse geometry-type key derived from an annotation's WKT `location`. Used to label
// the selection panel and adapt which measurements are shown. Note: Ims stores
// circles/ellipses as approximated polygons, so they are indistinguishable from a
// polygon here — same limitation the rest of the UI has.
export function geometryTypeKey(wkt) {
  if (!wkt) {
    return null;
  }

  const s = wkt.trim().toUpperCase();
  if (s.startsWith('POINT') || s.startsWith('MULTIPOINT')) {
    return 'point';
  }
  if (s.startsWith('LINESTRING') || s.startsWith('MULTILINESTRING')) {
    return 'line';
  }
  if (s.startsWith('MULTIPOLYGON')) {
    return 'polygon';
  }
  if (s.startsWith('POLYGON')) {
    return isRectangleWKT(s) ? 'rectangle' : 'polygon';
  }
  return null;
}

// Bounding-box width/height (in image pixels) of a WKT geometry. Returns null when it
// can't be parsed. Used to show a rectangle's dimensions in the selection panel — the
// panel only has the stored `location` WKT, so we derive the extent from its coordinates.
export function wktBBoxSize(wkt) {
  if (!wkt) {
    return null;
  }
  const nums = wkt.match(/-?\d+(?:\.\d+)?/g);
  if (!nums || nums.length < 4) {
    return null;
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = parseFloat(nums[i]);
    const y = parseFloat(nums[i + 1]);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return {width: maxX - minX, height: maxY - minY};
}
